import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '@components/core/Tooltip';
import useRovingFocus, { ROVING_ORIENTATION } from '@hooks/useRovingFocus';

/**
 * Two-or-more-way pill toggle (design parity with automation condition UI) —
 * gray track + white active segment. size: 'md' (default) | 'sm'.
 * Options may carry a per-option `disabled` (rendered disabled, skipped by
 * keyboard navigation) and `tooltip`.
 * Uses buttons + radiogroup semantics with a roving tabindex — arrows move
 * BOTH selection and focus (wrapping), Home/End jump to the first/last enabled
 * segment; when `value` matches no option, the first enabled segment stays
 * tabbable so the control is always keyboard-reachable.
 * Styles: ./SegmentedControl.less is wired into less/ats_style.less (the
 * white-label pipeline) — no JSX .less import, per frontend-structure.mdc.
 */

const SIZES = ['sm', 'md'];

const SegmentedControl = ({
    options,
    value,
    onChange,
    ariaLabel = null,
    className = null,
    size = 'md',
    disabled = false,
    compact = false,
    ...rest
}) => {
    const isOptionDisabled = (opt) => disabled || Boolean(opt?.disabled);

    const { getItemRef, handleKeyDown, tabbableIndex } = useRovingFocus({
        count: options?.length ?? 0,
        activeIndex: options?.findIndex((opt) => opt.id === value) ?? -1,
        orientation: ROVING_ORIENTATION.HORIZONTAL,
        isDisabled: (index) => isOptionDisabled(options[index]),
        onSelect: (index) => onChange(options[index].id),
    });

    if (!options?.length) return null;

    return (
        <div
            className={classNames(
                'segmented-control',
                `segmented-control--${SIZES.includes(size) ? size : 'md'}`,
                { 'segmented-control--compact': compact },
                className,
            )}
            role="radiogroup"
            aria-label={ariaLabel}
            {...rest}
        >
            {options.map((opt, index) => {
                const active = value === opt.id;
                const optDisabled = isOptionDisabled(opt);
                const segment = (
                    <button
                        ref={getItemRef(index)}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        tabIndex={index === tabbableIndex ? 0 : -1}
                        disabled={optDisabled}
                        className={classNames('segmented-control__segment', { 'is-active': active })}
                        onClick={() => !optDisabled && onChange(opt.id)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                    >
                        {opt.label}
                    </button>
                );
                return (
                    <div key={opt.id} className="segmented-control__segment-wrap">
                        {opt.tooltip ? <Tooltip title={opt.tooltip}>{segment}</Tooltip> : segment}
                    </div>
                );
            })}
        </div>
    );
};

SegmentedControl.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.node.isRequired,
            tooltip: PropTypes.string,
            disabled: PropTypes.bool,
        }),
    ).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(SIZES),
    disabled: PropTypes.bool,
    compact: PropTypes.bool,
};

export default SegmentedControl;
